import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import './DoctorStats.css';

const formatToday = () => new Date().toISOString().split('T')[0];
const formatMonthStart = () => {
  const d = new Date();
  const monthStart = new Date(d.getFullYear(), d.getMonth(), 1);
  return monthStart.toISOString().split('T')[0];
};

export default function DoctorStats() {
  const API_URL = process.env.REACT_APP_API_URL;
  const [startDate, setStartDate] = useState(formatMonthStart());
  const [endDate, setEndDate] = useState(formatToday());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({ new_patients: 0, appointments: 0, total_income: 0, start_date: '', end_date: '' });

  const canQuery = useMemo(() => {
    if (!startDate || !endDate) return true; // allow querying even if empty to use backend defaults
    try {
      return new Date(startDate) <= new Date(endDate);
    } catch { return true; }
  }, [startDate, endDate]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError(null);
            
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);

      const res = await axios.get(`${API_URL}doctor/stats?${params.toString()}`, {
        headers: { 'Accept': 'application/json' }
      });
      setStats(res.data || {});
    } catch (err) {
      console.error('Failed to fetch stats', err);
      setError(err.response?.data?.message || err.message || 'Failed to fetch stats');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleQuickRange = (type) => {
    const today = new Date();
    if (type === 'today') {
      const t = today.toISOString().split('T')[0];
      setStartDate(t);
      setEndDate(t);
    } else if (type === 'week') {
      const start = new Date(today);
      start.setDate(today.getDate() - 6);
      setStartDate(start.toISOString().split('T')[0]);
      setEndDate(today.toISOString().split('T')[0]);
    } else if (type === 'month') {
      setStartDate(formatMonthStart());
      setEndDate(formatToday());
    }
  };

  const StatCard = ({ title, value, icon, accent }) => (
    <div className="col-12 col-md-4 mb-3">
      <div className={`stats-card ${accent}`}>
        <div className="stats-icon"><i className={icon}></i></div>
        <div className="stats-content">
          <div className="stats-title">{title}</div>
          <div className="stats-value">{value}</div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="container mt-4">
      <div className="row justify-content-center">
        <div className="col-12">
          <div className="card patient-list-card">
            <div className="card-header patient-list-header d-flex flex-wrap justify-content-between align-items-center">
              <h4 className="mb-2 mb-md-0">Clinic Statistics</h4>
              <div className="d-flex flex-wrap gap-2">
                <button className="btn btn-sm btn-outline-light" onClick={() => handleQuickRange('today')}>
                  <i className="fas fa-bolt me-1"></i> Today
                </button>
                <button className="btn btn-sm btn-outline-light" onClick={() => handleQuickRange('week')}>
                  <i className="fas fa-calendar-week me-1"></i> Last 7 days
                </button>
                <button className="btn btn-sm btn-outline-light" onClick={() => handleQuickRange('month')}>
                  <i className="fas fa-calendar-alt me-1"></i> This month
                </button>
              </div>
            </div>
            <div className="card-body patient-list-body">
              {/* Filters */}
              <div className="row g-3 align-items-end mb-3">
                <div className="col-12 col-md-4">
                  <label className="form-label small text-muted">From</label>
                  <input type="date" className="form-control date-input" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                </div>
                <div className="col-12 col-md-4">
                  <label className="form-label small text-muted">To</label>
                  <input type="date" className="form-control date-input" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                </div>
                <div className="col-12 col-md-4 d-flex gap-2">
                  <button className="btn search-btn flex-fill" disabled={!canQuery || loading} onClick={fetchStats}>
                    {loading ? (<><span className="spinner-border spinner-border-sm me-2"></span>Loading...</>) : (<><i className="fas fa-sync-alt me-2"></i>Refresh</>)}
                  </button>
                  <button className="btn btn-outline-secondary flex-fill" disabled={loading} onClick={() => { setStartDate(formatMonthStart()); setEndDate(formatToday()); }}>
                    <i className="fas fa-undo me-2"></i>Reset
                  </button>
                </div>
              </div>

              {error && (
                <div className="alert alert-danger" role="alert">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  {error}
                </div>
              )}

              {/* Stats */}
              <div className="row">
                <StatCard title="New Patients" value={stats?.new_patients ?? 0} icon="fas fa-user-plus" accent="accent-blue" />
                <StatCard title="Appointments" value={stats?.appointments ?? 0} icon="fas fa-notes-medical" accent="accent-purple" />
                <StatCard title="Total Income" value={`${Number(stats?.total_income ?? 0).toLocaleString()} IQD`} icon="fas fa-coins" accent="accent-green" />
              </div>

              {/* Range info */}
              <div className="range-pill mt-3">
                <i className="fas fa-calendar me-2"></i>
                Showing stats for: <strong className="ms-1">{stats?.start_date || startDate}</strong>
                <span className="mx-2">→</span>
                <strong>{stats?.end_date || endDate}</strong>
              </div>

              {/* Tip */}
              <div className="small text-muted mt-3">
                Tip: Set future dates only for appointments. Completed medical records should remain on their actual service date.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
